var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { computed, ref, defineComponent } from 'vue';
import VcSlider from '../vc-slider/src/Slider';
import VcRange from '../vc-slider/src/Range';
import VcHandle from '../vc-slider/src/Handle';
import { stringType, booleanType, someType, objectType, withInstall, functionType, } from '../_util/type';
import useConfigInject from '../config-provider/hooks/useConfigInject';
import SliderTooltip from './SliderTooltip';
import classNames from '../_util/classNames';
import { useInjectFormItemContext } from '../form/FormItemContext';
// CSSINJS
import useStyle from './style';
import devWarning from '../vc-util/devWarning';
const defaultTipFormatter = (value) => (typeof value === 'number' ? value.toString() : '');
export const sliderProps = () => ({
    id: String,
    prefixCls: String,
    tooltipPrefixCls: String,
    range: someType([Boolean, Object]),
    reverse: booleanType(),
    min: Number,
    max: Number,
    step: someType([Object, Number]),
    marks: objectType(),
    dots: booleanType(),
    value: someType([Array, Number]),
    defaultValue: someType([Array, Number]),
    included: booleanType(),
    disabled: booleanType(),
    vertical: booleanType(),
    /** @deprecated Prefer `tooltip.formatter` */
    tipFormatter: someType([Function, Object], () => defaultTipFormatter),
    /** @deprecated Prefer `tooltip.open` */
    tooltipOpen: booleanType(),
    /** @deprecated Prefer `tooltip.open` */
    tooltipVisible: booleanType(),
    /** @deprecated Prefer `tooltip.placement` */
    tooltipPlacement: stringType(),
    /** @deprecated Prefer `tooltip.getPopupContainer` */
    getTooltipPopupContainer: functionType(),
    /** Tooltip config object (antd ≥ 5) */
    tooltip: objectType(),
    autofocus: booleanType(),
    handleStyle: someType([Array, Object]),
    trackStyle: someType([Array, Object]),
    onChange: functionType(),
    onAfterChange: functionType(),
    onFocus: functionType(),
    onBlur: functionType(),
    'onUpdate:value': functionType(),
});
const Slider = defineComponent({
    compatConfig: { MODE: 3 },
    name: 'ASlider',
    inheritAttrs: false,
    props: sliderProps(),
    // emits: ['update:value', 'change', 'afterChange', 'blur'],
    slots: Object,
    setup(props, { attrs, slots, emit, expose }) {
        // Warning for deprecated usage
        if (process.env.NODE_ENV !== 'production') {
            [
                ['tooltipVisible', 'tooltip.open'],
                ['tooltipOpen', 'tooltip.open'],
                ['tooltipPlacement', 'tooltip.placement'],
                ['getTooltipPopupContainer', 'tooltip.getPopupContainer'],
                ['tooltipPrefixCls', 'tooltip.prefixCls'],
            ].forEach(([deprecatedName, newName]) => {
                devWarning(props[deprecatedName] === undefined, 'Slider', `\`${deprecatedName}\` is deprecated, please use \`${newName}\` instead.`);
            });
        }
        const { prefixCls, rootPrefixCls, direction, getPopupContainer, configProvider } = useConfigInject('slider', props);
        // style
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const formItemContext = useInjectFormItemContext();
        const sliderRef = ref();
        const visibles = ref({});
        const toggleTooltipOpen = (index, visible) => {
            visibles.value[index] = visible;
        };
        const mergedTooltip = computed(() => {
            var _a, _b;
            const tip = props.tooltip || {};
            const formatter = tip.formatter !== undefined
                ? tip.formatter
                : props.tipFormatter !== undefined
                    ? props.tipFormatter
                    : defaultTipFormatter;
            const open = tip.open !== undefined
                ? tip.open
                : props.tooltipOpen !== undefined
                    ? props.tooltipOpen
                    : props.tooltipVisible;
            const placement = (_a = tip.placement) !== null && _a !== void 0 ? _a : props.tooltipPlacement;
            const getContainer = (_b = tip.getPopupContainer) !== null && _b !== void 0 ? _b : props.getTooltipPopupContainer;
            return {
                formatter,
                open,
                placement,
                getPopupContainer: getContainer,
                autoAdjustOverflow: tip.autoAdjustOverflow,
                prefixCls: tip.prefixCls,
            };
        });
        const tooltipPlacement = computed(() => {
            if (mergedTooltip.value.placement) {
                return mergedTooltip.value.placement;
            }
            if (!props.vertical) {
                return 'top';
            }
            return direction.value === 'rtl' ? 'left' : 'right';
        });
        const focus = () => {
            var _a;
            (_a = sliderRef.value) === null || _a === void 0 ? void 0 : _a.focus();
        };
        const blur = () => {
            var _a;
            (_a = sliderRef.value) === null || _a === void 0 ? void 0 : _a.blur();
        };
        const handleChange = (val) => {
            emit('update:value', val);
            emit('change', val);
            formItemContext.onFieldChange();
        };
        const handleBlur = (e) => {
            emit('blur', e);
        };
        expose({
            focus,
            blur,
        });
        const handleWithTooltip = (_a) => {
            var { tooltipPrefixCls } = _a, _b = _a.info, { value, dragging, index } = _b, restProps = __rest(_b, ["value", "dragging", "index"]);
            const { formatter: tipFormatter, open: tooltipOpen, getPopupContainer: tipGetPopup, } = mergedTooltip.value;
            const isTipFormatter = tipFormatter ? visibles.value[index] || dragging : false;
            const open = tooltipOpen || (tooltipOpen === undefined && isTipFormatter);
            return (<SliderTooltip prefixCls={tooltipPrefixCls} title={tipFormatter ? tipFormatter(value) : ''} open={open} placement={tooltipPlacement.value} autoAdjustOverflow={mergedTooltip.value.autoAdjustOverflow} transitionName={`${rootPrefixCls.value}-zoom-down`} key={index} overlayClassName={`${prefixCls.value}-tooltip`} getPopupContainer={tipGetPopup || (getPopupContainer === null || getPopupContainer === void 0 ? void 0 : getPopupContainer.value)}>
          <VcHandle {...restProps} value={value} onMouseenter={() => toggleTooltipOpen(index, true)} onMouseleave={() => toggleTooltipOpen(index, false)}/>
        </SliderTooltip>);
        };
        return () => {
            const { tooltipPrefixCls: customizeTooltipPrefixCls, range, id = formItemContext.id.value, tooltip: _tooltip, tipFormatter: _tipFormatter, tooltipOpen: _tooltipOpen, tooltipVisible: _tooltipVisible, tooltipPlacement: _tooltipPlacement, getTooltipPopupContainer: _getTooltipPopupContainer } = props, restProps = __rest(props, ["tooltipPrefixCls", "range", "id", "tooltip", "tipFormatter", "tooltipOpen", "tooltipVisible", "tooltipPlacement", "getTooltipPopupContainer"]);
            const tooltipPrefixCls = configProvider.getPrefixCls('tooltip', mergedTooltip.value.prefixCls || customizeTooltipPrefixCls);
            const cls = classNames(attrs.class, {
                [`${prefixCls.value}-rtl`]: direction.value === 'rtl',
            }, hashId.value);
            // make reverse default on rtl direction
            if (direction.value === 'rtl' && !restProps.vertical) {
                restProps.reverse = !restProps.reverse;
            }
            // extrack draggableTrack from range={{ ... }}
            let draggableTrack;
            if (typeof range === 'object') {
                draggableTrack = range.draggableTrack;
            }
            if (range) {
                return wrapSSR(<VcRange {...attrs} {...restProps} step={restProps.step} draggableTrack={draggableTrack} class={cls} ref={sliderRef} handle={(info) => handleWithTooltip({
                        tooltipPrefixCls,
                        prefixCls: prefixCls.value,
                        info,
                    })} prefixCls={prefixCls.value} onChange={handleChange} onBlur={handleBlur} v-slots={{ mark: slots.mark }}/>);
            }
            return wrapSSR(<VcSlider {...attrs} {...restProps} id={id} step={restProps.step} class={cls} ref={sliderRef} handle={(info) => handleWithTooltip({
                    tooltipPrefixCls,
                    prefixCls: prefixCls.value,
                    info,
                })} prefixCls={prefixCls.value} onChange={handleChange} onBlur={handleBlur} v-slots={{ mark: slots.mark }}/>);
        };
    },
});
export default withInstall(Slider);
